import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api, { formatRupiah } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Heart, X, Info, SpeakerHigh, CurrencyCircleDollar } from '@phosphor-icons/react';

export default function TipModal({ open, onOpenChange, creator, units, selectedUnitId, ttsInfo, mediaInfo }) {
  const [unitId, setUnitId] = useState(selectedUnitId || (units[0]?.unit_id));
  const [qty, setQty] = useState(1);
  const [supporterName, setSupporterName] = useState('');
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [useCustom, setUseCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setUnitId(selectedUnitId || (units[0]?.unit_id));
      setQty(1);
      setMessage('');
      setMediaUrl('');
      setIsPublic(true);
      setUseCustom(false);
      setCustomAmount('');
    }
  }, [open, selectedUnitId, units]);

  const unit = units.find((u) => u.unit_id === unitId);
  const total = useCustom 
    ? (parseInt(customAmount) || 0) 
    : (unit ? unit.price * qty : 0);

  const ttsMinAmount = ttsInfo?.tts_min_amount || 0;
  const ttsActive = ttsInfo?.tts_enabled || false;
  const willTTS = ttsActive && total >= ttsMinAmount && message.trim().length > 0;

  const quickAmounts = [5000, 10000, 25000, 50000, 100000];

  const submit = async (e) => {
    e.preventDefault();
    if (!unit) return;
    if (useCustom && (!customAmount || parseInt(customAmount) < 1000)) {
      toast.error('Nominal minimal Rp 1.000');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        username: creator.username,
        unit_id: useCustom ? null : unit.unit_id,
        qty: useCustom ? 1 : qty,
        supporter_name: supporterName.trim() || 'Anonim',
        message: message.trim(),
        is_public: isPublic,
        media_url: mediaUrl.trim(),
      };
      if (useCustom) {
        payload.custom_amount = parseInt(customAmount);
      }
      const res = await api.post('/checkout', payload);
      onOpenChange(false);
      navigate(`/pay/${res.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat transaksi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-[calc(100%-2rem)] h-[100dvh] sm:h-auto sm:max-h-[90dvh] border-2 border-foreground bg-white shadow-brutal-lg sm:rounded-none p-0 flex flex-col overflow-hidden"
        data-testid="tip-modal"
      >
        {/* HEADER */}
        <div className="shrink-0 border-b-2 border-foreground bg-primary text-white p-5 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-bold opacity-90">Traktir</p>
            <DialogTitle className="font-display text-2xl font-bold mt-1">
              {creator.name}
            </DialogTitle>
          </div>
          <button 
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 border-2 border-white/20 bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Pilih Unit</label>
              <div className="grid grid-cols-3 gap-2">
                {units.map((u) => (
                  <button
                    type="button"
                    key={u.unit_id}
                    onClick={() => { setUnitId(u.unit_id); setUseCustom(false); }}
                    className={`border-2 border-foreground p-3 text-center transition-all ${
                      unitId === u.unit_id && !useCustom ? 'bg-accent shadow-none translate-x-[2px] translate-y-[2px]' : 'bg-white shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px]'
                    }`}
                  >
                    <div className="text-2xl">{u.emoji || '☕'}</div>
                    <div className="text-xs font-bold mt-1">{u.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* MODE TOGGLE: Unit × Qty  OR  Custom Amount */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setUseCustom(false)}
                className={`flex-1 border-2 border-foreground py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                  !useCustom ? 'bg-foreground text-background' : 'bg-white hover:bg-background/50'
                }`}
              >
                Per Unit
              </button>
              <button
                type="button"
                onClick={() => setUseCustom(true)}
                className={`flex-1 border-2 border-foreground py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                  useCustom ? 'bg-foreground text-background' : 'bg-white hover:bg-background/50'
                }`}
              >
                <CurrencyCircleDollar size={14} weight="bold" className="inline mr-1 -mt-0.5" />
                Tulis Nominal
              </button>
            </div>

            {!useCustom ? (
              /* STANDARD: Qty selector */
              <div>
                <label className="text-xs uppercase tracking-widest font-bold block mb-2">Jumlah</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-10 h-10 border-2 border-foreground bg-white shadow-brutal-sm flex items-center justify-center hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none">
                    <Minus size={16} weight="bold" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || '1')))}
                    className="flex-1 text-center border-2 border-foreground bg-white px-4 py-2 font-bold text-lg shadow-brutal-sm focus:outline-none focus:border-primary"
                  />
                  <button type="button" onClick={() => setQty(Math.min(999, qty + 1))}
                    className="w-10 h-10 border-2 border-foreground bg-white shadow-brutal-sm flex items-center justify-center hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none">
                    <Plus size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ) : (
              /* CUSTOM: Free nominal input */
              <div>
                <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nominal (Rp)</label>
                <input
                  type="number"
                  min="1000"
                  step="1000"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Minimal Rp 1.000"
                  className="w-full border-2 border-foreground bg-white px-4 py-3 font-bold text-lg shadow-brutal-sm focus:outline-none focus:border-primary"
                />
                {/* Quick amount buttons */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {quickAmounts.map((amt) => (
                    <button
                      type="button"
                      key={amt}
                      onClick={() => setCustomAmount(String(amt))}
                      className={`border-2 border-foreground px-3 py-1.5 text-xs font-bold transition-all ${
                        parseInt(customAmount) === amt 
                          ? 'bg-accent shadow-none translate-x-[1px] translate-y-[1px]' 
                          : 'bg-white shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none'
                      }`}
                    >
                      {formatRupiah(amt)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* TTS Info Banner */}
            {ttsActive && (
              <div className={`border-2 border-foreground p-3 flex items-start gap-2 ${willTTS ? 'bg-accent/30' : 'bg-secondary/30'}`}>
                <SpeakerHigh size={18} weight="bold" className="shrink-0 mt-0.5" />
                <div>
                  {willTTS ? (
                    <p className="text-xs font-bold text-foreground">
                      🔊 Pesanmu akan dibacakan di stream!
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {ttsMinAmount > 0 
                        ? `Donasi minimal ${formatRupiah(ttsMinAmount)} + tulis pesan agar pesanmu dibacakan di stream (TTS).`
                        : 'Tulis pesan agar pesanmu dibacakan di stream (TTS).'}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Namamu</label>
              <input
                value={supporterName}
                onChange={(e) => setSupporterName(e.target.value)}
                placeholder="Anonim"
                maxLength={50}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Pesan (opsional)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                maxLength={280}
                placeholder="Tulis pesan semangat..."
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">{message.length}/280</p>
            </div>

            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 border-2 border-foreground"
              />
              Tampilkan di supporter feed publik
            </label>

            {mediaInfo && (
              <div>
                <label className="text-xs uppercase tracking-widest font-bold block mb-2">Media Share (YouTube / TikTok)</label>
                {mediaInfo.media_share_enabled ? (
                  <>
                    <input
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      disabled={total < mediaInfo.media_share_min_amount}
                      placeholder={total < mediaInfo.media_share_min_amount 
                        ? `Minimal donasi ${formatRupiah(mediaInfo.media_share_min_amount)}` 
                        : "Paste link YouTube atau TikTok..."}
                      maxLength={500}
                      className={`w-full border-2 border-foreground px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary text-sm ${
                        total < mediaInfo.media_share_min_amount ? 'bg-muted cursor-not-allowed opacity-60' : 'bg-white'
                      }`}
                    />
                    <div className="mt-1 space-y-1">
                      {total < mediaInfo.media_share_min_amount ? (
                        <p className="text-[10px] text-muted-foreground font-bold">
                          🚫 Tambah nominal hingga {formatRupiah(mediaInfo.media_share_min_amount)} untuk aktifkan Media Share.
                        </p>
                      ) : mediaUrl.trim() ? (
                        <p className="text-[10px] text-green-600 font-bold italic">
                          ✅ Video akan diputar selama {Math.min(mediaInfo.media_share_max_duration, Math.floor(total / mediaInfo.media_share_price_per_sec))} detik ({formatRupiah(mediaInfo.media_share_price_per_sec)}/detik)
                        </p>
                      ) : (
                        <p className="text-[10px] text-primary font-bold italic leading-tight">
                          💡 Donasi {formatRupiah(total)}: Kamu bisa putar video selama <u>{Math.min(mediaInfo.media_share_max_duration, Math.floor(total / mediaInfo.media_share_price_per_sec))} detik</u>. Support: YouTube, TikTok.
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-[10px] text-muted-foreground italic bg-muted p-2 border-2 border-foreground/10">
                    Fitur Media Share sedang dinonaktifkan oleh kreator.
                  </p>
                )}
              </div>
            )}

            {/* SUMMARY */}
            <div className="border-2 border-foreground bg-primary/5 p-4 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold uppercase opacity-60">Total Dukungan</span>
                <span className="font-display text-xl font-bold">{formatRupiah(total)}</span>
              </div>
              <p className="text-[9px] text-muted-foreground italic leading-tight">
                *Belum termasuk biaya admin QRIS (Pakasir) yang akan ditambahkan otomatis di halaman pembayaran.
              </p>
            </div>
          </div>

          {/* STICKY FOOTER */}
          <div className="shrink-0 border-t-2 border-foreground p-4 bg-white flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Bayar</p>
              <p className="font-display text-xl font-bold truncate">{formatRupiah(total)}</p>
            </div>
            <button
              type="submit"
              disabled={loading || !unit || (useCustom && (!customAmount || parseInt(customAmount) < 1000))}
              className="inline-flex items-center justify-center gap-2 border-2 border-foreground bg-primary text-white px-5 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60 whitespace-nowrap text-sm"
            >
              <Heart size={16} weight="fill" />
              {loading ? 'Proses...' : 'Bayar QRIS'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
